import React from 'react';
import { Grid, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { observer } from 'mobx-react';
import CloseIcon from '@mui/icons-material/Close';
import { Pages, Tabs, UiStore } from '@gamiphy/loyalty-station-desktop/store/ui';
import { ProfileCard } from '@gamiphy/loyalty-station-desktop/components/home/profile';
import { SurveyContent } from '@gamiphy/loyalty-station-desktop/components/survey/content';
import { useMobile } from '@gamiphy/loyalty-station-desktop/utils/mobile';
import { WelcomeGiftPage } from '@gamiphy/loyalty-station-desktop/pages/pointsProgram/weclomeGift';
import { WelcomeGiftCongrats } from '@gamiphy/loyalty-station-desktop/components/welcomeGiftCongrats';
import { RedeemHistoryPage } from '@gamiphy/loyalty-station-desktop/pages/pointsProgram/redeemHistory';
import { DraftUserPage } from '@gamiphy/loyalty-station-desktop/pages/pointsProgram/draftUser';
import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import { HomePage } from '@gamiphy/loyalty-station-desktop/pages/pointsProgram/home';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import RedeemCard from '@gamiphy/loyalty-station-desktop/components/redeem/card/RedeemCard';
import PoweredByCoretava from '@gamiphy/icons/PoweredByCoretava';
import { SurveyStore } from '@gamiphy/loyalty-station-desktop/store/survey';
import { LandingPage } from '@gamiphy/loyalty-station-desktop/pages/pointsProgram/landing';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';

const PointsMainContent: React.FC = observer(() => {
  const [checkLater, setCheckLater] = React.useState<boolean>(false);
  const isMobile = useMobile();
  const theme = useTheme();
  const onDone = React.useCallback(() => {
    if (UiStore.currentPage !== Pages.welcomeGiftCongrats) {
      UiStore.closePage();
    }
  }, [UiStore.currentPage]);

  const ArrowIcon = React.useMemo(() => {
    if (theme.direction === 'rtl') {
      return <ArrowForward sx={{ fontSize: '2.4rem', display: 'flex', color: '#fff' }} />;
    }
    return <ArrowBack sx={{ fontSize: '2.4rem', display: 'flex', color: '#fff' }} />;
  }, [theme.direction]);

  const isBackButton = React.useMemo(() => {
    switch (UiStore.currentPage) {
      case Pages.redeemHistory:
      case Pages.welcomeGift:
      case Pages.survey:
      case Pages.welcomeGiftCongrats:
        return true;
      default:
        return false;
    }
  }, [UiStore.currentPage]);

  const handleClick = React.useCallback(() => {
    if (isBackButton) {
      UiStore.closePage();
    } else {
      UiStore.closePage();
      UiStore.close();
    }
  }, [isBackButton]);

  const defaultPage = React.useMemo(() => {
    if (UiStore.currentTab !== Tabs.WelcomeGift) {
      setCheckLater(true);
    }
    switch (true) {
      case UserStore.isDraftUser && !checkLater:
        return <DraftUserPage setCheckLater={setCheckLater} />;
      default:
        return <HomePage />;
    }
  }, [UserStore.isDraftUser, checkLater, UiStore.currentTab]);

  const ContentComponent = React.useMemo<React.ReactNode>(() => {
    switch (UiStore.currentPage) {
      case Pages.landing:
        return <LandingPage />;
      case Pages.redeemHistory:
        return <RedeemHistoryPage />;
      case Pages.welcomeGift:
        return <WelcomeGiftPage />;
      case Pages.survey:
        return <SurveyContent onDone={onDone} appSurvey={SurveyStore.appSurvey} />;
      case Pages.welcomeGiftCongrats:
        return <WelcomeGiftCongrats />;
      default:
        return defaultPage;
    }
  }, [UiStore.currentPage, defaultPage, SurveyStore.appSurvey]);

  return (
    <Grid container direction="column" wrap="nowrap" sx={{ backgroundColor: '#fafafa', height: '100%', width: '100%' }}>
      {
        isMobile && (
          <Grid
            onClick={handleClick}
            sx={{
              display: 'flex',
              backgroundColor: '#7c7c7cb3',
              borderRadius: 7,
              padding: theme.spacing(1),
              position: 'absolute',
              zIndex: 10,
              top: theme.spacing(3),
              right: theme.spacing(2),
              '&:hover': {
                cursor: 'pointer',
              },
            }}
          >
            {isBackButton ? ArrowIcon : <CloseIcon sx={{ fontSize: '2.4rem', display: 'flex', color: '#fff' }} />}
          </Grid>
        )
      }
      <Grid display="flex">
        <ProfileCard isBackButton={isBackButton} handleClick={handleClick} />
      </Grid>
      <Grid sx={{
        overflow: 'auto', display: 'flex', flex: '1 1 auto', position: 'relative',
      }}
      >
        {ContentComponent}
      </Grid>
      {UiStore.currentPage !== Pages.landing && (
        <Grid sx={{ boxShadow: '0 -6px 12px 0 rgba(0, 0, 0, 0.1)' }}>
          <RedeemCard />
        </Grid>
      )}
      {!AppStore.app.featureFlags.hidePoweredByCoretava && (
      <>
        <Divider />
        <Grid
          bgcolor="#fff"
          p={1}
          textAlign="center"
        >
          <a href="https://coretava.com" target="_blank" rel="noreferrer" aria-label="Coretava">
            <PoweredByCoretava height="2rem" />
          </a>
        </Grid>
      </>
      )}
    </Grid>
  );
});

export default PointsMainContent;
